/**
 * pdpSectionSlider – Wiederverwendbare Alpine.js Slider-Komponente
 * für alle PDP 2026 Sections (pdp-feature-cards, pdp-ingredients, pdp-reviews, etc.)
 *
 * Nutzung in einer Section:
 *   x-data="pdpSectionSlider({ slidesPerViewMobile: N, slidesPerViewDesktop: N, enabled: bool })"
 *
 * Benötigte HTML-Struktur:
 *   x-ref="track"             → auf dem Scroll-Container
 *   class="pdp-slider__slide" → auf jedem einzelnen Slide
 *
 * Warum init() + $nextTick:
 *   x-show="maxSlide > 0" auf den Arrows wird in DOM-Reihenfolge vor x-ref="track"
 *   ausgewertet. Zu dem Zeitpunkt ist $refs.track noch undefined → maxSlide = 0.
 *   Deshalb wird _slideCount als reaktive Property gesetzt – erst in $nextTick,
 *   wenn alle x-ref Elemente von Alpine registriert sind.
 */
function pdpSectionSlider({ slidesPerViewMobile = 1, slidesPerViewDesktop = 3, gap = 20, enabled = true } = {}) {
  return {
    _enabled: enabled,
    activeSlide: 0,
    _slideCount: 0,   // reaktive Property – wird in init() gesetzt
    _isDesktop: false, // reaktive Property – für responsive maxSlide

    init() {
      // Nach Alpine-Init sind alle x-ref Elemente verfügbar
      this.$nextTick(() => {
        this._isDesktop = window.matchMedia('(min-width: 720px)').matches;
        this._slideCount = this.$refs.track
          ? this.$refs.track.querySelectorAll(':scope > .pdp-slider__slide').length
          : 0;
      });

      // Viewport-Änderungen (Resize) reaktiv halten
      window.addEventListener('resize', () => {
        this._isDesktop = window.matchMedia('(min-width: 720px)').matches;
      });
    },

    get _slidesPerView() {
      return this._isDesktop ? slidesPerViewDesktop : slidesPerViewMobile;
    },

    get maxSlide() {
      return Math.max(0, this._slideCount - this._slidesPerView);
    },

    get canPrev() {
      return this._enabled && this.activeSlide > 0;
    },

    get canNext() {
      return this._enabled && this.activeSlide < this.maxSlide;
    },

    onScroll() {
      if (!this._enabled) return;
      const track = this.$refs.track;
      const slide = track && track.querySelector('.pdp-slider__slide');
      if (!track || !slide) return;
      const stepWidth = slide.offsetWidth + gap;
      this.activeSlide = Math.min(this.maxSlide, Math.round(track.scrollLeft / stepWidth));
    },

    scrollToSlide(i) {
      if (!this._enabled) return;
      const track = this.$refs.track;
      const slide = track && track.querySelector('.pdp-slider__slide');
      if (!track || !slide) return;
      const stepWidth = slide.offsetWidth + gap;
      track.scrollTo({ left: i * stepWidth, behavior: 'smooth' });
      this.activeSlide = Math.min(i, this.maxSlide);
    },

    prev() { this.scrollToSlide(Math.max(0, this.activeSlide - 1)); },
    next() { this.scrollToSlide(Math.min(this.maxSlide, this.activeSlide + 1)); },

    dotsArray() {
      return Array.from({ length: this.maxSlide + 1 }, (_, i) => i);
    }
  };
}
